import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Radio,
  Truck,
  Zap,
  ArrowRight,
  Activity,
  Map,
  Settings,
  BarChart2,
  Shield,
  Wifi,
  CheckCircle,
  Clock,
  Gauge,
  Cable,
  LineChart
} from 'lucide-react';

const MetricCard = ({ icon: Icon, value, label, trend }) => (
  <motion.div
    className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 flex flex-col items-center text-center"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    <div className="flex flex-col items-center gap-4 mb-4">
      <div className="p-3 rounded-lg bg-indigo-500/10">
        <Icon className="w-6 h-6 text-indigo-400" />
      </div>
      {trend && (
        <div className="bg-green-500/10 px-2 py-1 rounded-full flex items-center gap-1">
          <LineChart className="w-4 h-4 text-green-400" />
          <span className="text-green-400 text-sm">{trend}</span>
        </div>
      )}
    </div>
    <div className="text-3xl font-bold text-white mb-2">{value}</div>
    <p className="text-gray-400">{label}</p>
  </motion.div>
);

const FeatureCard = ({ icon: Icon, title, description }) => (
  <motion.div
    className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-indigo-500/50 transition-colors flex flex-col items-center text-center"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    <div className="bg-indigo-500/10 rounded-lg p-3 w-12 h-12 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-indigo-400" />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
    <p className="text-gray-300">{description}</p>
  </motion.div>
);

const ProviderCard = ({ logo, name, status }) => (
  <motion.div
    className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 flex flex-col items-center text-center"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ borderColor: "rgba(99,102,241,0.5)" }}
  >
    <div className="w-16 h-16 mb-4 rounded-lg bg-white/10 flex items-center justify-center">
      {logo}
    </div>
    <h3 className="text-lg font-semibold text-white mb-2">{name}</h3>
    <span className={`px-3 py-1 rounded-full text-sm ${
      status === 'Available' 
        ? 'bg-green-500/10 text-green-400' 
        : 'bg-yellow-500/10 text-yellow-400'
    }`}>
      {status}
    </span>
  </motion.div>
);

const TelematicsIntegration = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-indigo-900 to-purple-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-block mb-6">
              <div className="flex items-center gap-2 text-sm text-indigo-400 bg-indigo-500/10 rounded-full px-4 py-1.5 border border-indigo-500/20">
                <Radio className="w-4 h-4" />
                Telematics Integration
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Universal Fleet Visibility
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Seamlessly integrate with leading telematics providers or use our built-in 
              tracking solution for complete fleet visibility and control.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                View Integrations
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <MetricCard
              icon={Truck}
              value="100K+"
              label="Connected Vehicles"
              trend="+45%"
            />
            <MetricCard
              icon={Activity}
              value="99.99%"
              label="Uptime Reliability"
            />
            <MetricCard
              icon={Clock}
              value="10sec"
              label="Update Frequency"
            />
          </div>
        </div>
      </section>

      {/* Integration Partners */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Supported Providers
            </h2>
            <p className="text-gray-300">
              Connect with industry-leading telematics solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <ProviderCard
              logo={<Truck className="w-8 h-8 text-indigo-400" />}
              name="Built-in Tracking"
              status="Available"
            />
            <ProviderCard
              logo={<Truck className="w-8 h-8 text-indigo-400" />}
              name="Samsara"
              status="Available"
            />
            <ProviderCard
              logo={<Truck className="w-8 h-8 text-indigo-400" />}
              name="Geotab"
              status="Coming Soon"
            />
            <ProviderCard
              logo={<Truck className="w-8 h-8 text-indigo-400" />}
              name="KeepTruckin"
              status="Coming Soon"
            />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Universal Integration Features
            </h2>
            <p className="text-gray-300">
              Powerful capabilities across all supported providers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon={Map}
              title="Real-Time Tracking"
              description="Live vehicle location updates with detailed trip history and replay functionality."
            />
            <FeatureCard
              icon={Gauge}
              title="Performance Metrics"
              description="Monitor speed, idle time, harsh events, and fuel efficiency across your fleet."
            />
            <FeatureCard
              icon={Cable}
              title="Easy Integration"
              description="Quick setup process with automatic data synchronization and mapping."
            />
            <FeatureCard
              icon={BarChart2}
              title="Unified Dashboard"
              description="Single interface for all your telematics data, regardless of provider."
            />
            <FeatureCard
              icon={Shield}
              title="Secure Data"
              description="Enterprise-grade security with encrypted data transmission and storage."
            />
            <FeatureCard
              icon={Settings}
              title="Custom Alerts"
              description="Configurable notifications for events, geofencing, and performance thresholds."
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="p-6 border-b border-white/10">
              <h3 className="text-xl font-semibold text-white">Integration Benefits</h3>
              <p className="text-gray-400 mt-1">Why choose our telematics integration</p>
            </div>
            <div className="p-6 grid gap-6">
              {[
                {
                  title: "Provider Flexibility",
                  description: "Freedom to use existing telematics solutions or our built-in tracking."
                },
                {
                  title: "Unified Experience",
                  description: "Consistent interface and data format across all providers."
                },
                {
                  title: "Cost Efficiency",
                  description: "Leverage existing investments while gaining advanced capabilities."
                },
                {
                  title: "Future-Proof",
                  description: "Regular updates and new integrations to support your growing needs."
                }
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex gap-4 items-start"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-500/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white">{benefit.title}</h4>
                    <p className="text-gray-300 mt-1">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Connect Your Fleet?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Start tracking your vehicles with your preferred telematics provider today
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center"
              >
                Get Started Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TelematicsIntegration;